import { problemResponse, registry, undeclaredQueryRefused } from "../registry.js";
import { isoDateTime, z } from "../zod.js";
import {
  ImpersonationCreateRequest,
  ImpersonationCreateResponse,
  ImpersonationEndResponse,
  ImpersonationGrantView,
  ImpersonationListResponse,
  ImpersonationRedeemRequest,
  ImpersonationRedeemResponse,
  ImpersonationTokenResponse,
  LifecycleEventsResponse,
  PlatformAcceptInviteRequest,
  PlatformAcceptInviteResponse,
  PlatformBreakGlassRequest,
  PlatformBreakGlassResponse,
  PlatformCreateTenantRequest,
  PlatformLoginRequest,
  PlatformLoginResponse,
  PlatformMeResponse,
  PlatformPasswordResetCompleteRequest,
  PlatformPasswordResetCompleteResponse,
  PlatformPasswordResetRequest,
  PlatformRecoveryCodesResponse,
  PlatformRecoveryRegenerateRequest,
  PlatformResendAdminInviteRequest,
  PlatformRestoreRequest,
  PlatformSetSandboxRequest,
  PlatformStaffInviteRequest,
  PlatformStaffListResponse,
  PlatformStaffView,
  PlatformSubdomainCheckResponse,
  PlatformSuspendRequest,
  PlatformTenantDetail,
  PlatformTenantListResponse,
  PlatformTenantSummary,
  PlatformTotpConfirmRequest,
  PlatformTotpEnrollResponse,
  PlatformTotpRequest,
  PlatformTotpResponse,
  PlatformVerticalPackListResponse,
  PlatformHealthAlertListResponse,
  PlatformHealthBoardResponse,
  PlatformHealthSeriesWindow,
  PlatformLogSince,
  PlatformRequestErrorListResponse,
  PlatformSetHealthDrillRequest,
  PlatformTenantHealthResponse,
  TenantProbeResponse,
  PlatformAuditLogResponse,
  PlatformChainVerification,
  PlatformRevokeAllSessionsRequest,
  PlatformRevokeAllSessionsResponse,
  PlatformRevokeSessionsResponse,
  PlatformStaffPatchRequest,
  PlatformStaffTotpResetRequest,
  PlatformSupportResetResponse,
  PlatformTenantUserListResponse,
} from "./schemas.js";

// /v1/platform/* — module 15's console surface (PR #7 v0 subset). Every path
// here answers ONLY on admin.assessly.ai behind the disjoint staff cookie
// space (`__Host-assessly_psid`; spec 15 §5 — no tenant session is ever
// accepted); requests on tenant hosts 404. The one exception is the
// tenant-side impersonation redemption endpoint (spec 16 §3.1.4a), tagged
// `identity` and served on tenant subdomains.
//
// Idempotency: mutations under /v1/platform/* require Idempotency-Key against
// the staff-keyed store (spec 15 §5, §9.2) — except the /v1/platform/auth/*
// group, exempt for the same reasons as /v1/auth/* (spec 16 §5).

export const platformSessionCookieAuth = registry.registerComponent(
  "securitySchemes",
  "platformSessionCookie",
  {
    type: "apiKey",
    in: "cookie",
    name: "__Host-assessly_psid",
    description:
      "Opaque DB-backed platform staff session cookie (spec 15 §2.13; __Host- prefix, SameSite=Strict, admin host only). Disjoint from the tenant cookie space by construction.",
  },
);

const staffSecurity = [{ platformSessionCookie: [] }];

// Statuses the platform guard chain answers on any console route. The session
// guard refuses every host but admin with 404 before it reads a cookie, and a
// staff member still enrolling the mandatory TOTP reaches only the enrollment
// routes; every other route answers them 403 TOTP_ENROLLMENT_REQUIRED.
const notAdminHost = problemResponse("Requested on a host other than the admin host");
const staffForbidden = (what: string) =>
  problemResponse(`${what}, or TOTP_ENROLLMENT_REQUIRED while the caller's TOTP is unconfirmed`);

registry.register("PlatformStaffView", PlatformStaffView);
registry.register("PlatformLoginResponse", PlatformLoginResponse);
registry.register("PlatformTenantSummary", PlatformTenantSummary);
registry.register("PlatformTenantDetail", PlatformTenantDetail);
registry.register("ImpersonationGrantView", ImpersonationGrantView);

// --- staff auth ---

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/login",
  operationId: "platformLogin",
  tags: ["platform"],
  summary: "Platform staff password step",
  description:
    "Generic 401 INVALID_CREDENTIALS (no oracle). 423 ACCOUNT_LOCKED after 10 failures (15-minute lock). 429 TOO_MANY_ATTEMPTS with Retry-After past 10 steps a minute per client address or 20 in ten minutes per address (D-494). Active staff receive a single-use login_ticket for the TOTP step; totp_pending staff receive a restricted session cookie and step='totp_enrollment_required' (spec 15 §3.1).",
  request: { body: { content: { "application/json": { schema: PlatformLoginRequest } } } },
  responses: {
    200: {
      description: "Password accepted — proceed to TOTP (or enrollment)",
      content: { "application/json": { schema: PlatformLoginResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("INVALID_CREDENTIALS"),
    404: notAdminHost,
    423: problemResponse("ACCOUNT_LOCKED — locked_until extension + Retry-After"),
    429: problemResponse(
      "TOO_MANY_ATTEMPTS — over 10 password steps a minute from one client address or 20 in ten minutes for one address, with Retry-After (D-494)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/totp",
  operationId: "platformTotpStep",
  tags: ["platform"],
  summary: "Platform staff TOTP step — mints the staff session",
  description:
    "Accepts a 6-digit TOTP code (±1 step, replayed steps rejected via totp_last_step) or an unused recovery code. Success sets __Host-assessly_psid (12 h absolute / 60 min idle) and audits staff.login (spec 15 §3.1.4).",
  request: { body: { content: { "application/json": { schema: PlatformTotpRequest } } } },
  responses: {
    200: {
      description: "Session established (Set-Cookie)",
      content: { "application/json": { schema: PlatformTotpResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("LOGIN_TICKET_INVALID or INVALID_TOTP_CODE"),
    404: notAdminHost,
    423: problemResponse("ACCOUNT_LOCKED"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/logout",
  operationId: "platformLogout",
  tags: ["platform"],
  summary: "Revoke the current staff session",
  security: staffSecurity,
  responses: {
    204: { description: "Session revoked; Set-Cookie clears the cookie" },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/me",
  operationId: "platformMe",
  tags: ["platform"],
  summary: "Session probe for the console SPA guard",
  security: staffSecurity,
  responses: {
    200: {
      description: "The authenticated staff member",
      content: { "application/json": { schema: PlatformMeResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/accept-invite",
  operationId: "platformAcceptInvite",
  tags: ["platform"],
  summary: "Validate a staff invite token and set the password",
  description:
    "Staff password policy: min 12 chars AND zxcvbn score ≥ 3 (spec 15 §3.1.2). Success moves the staff member to totp_pending — no console access until TOTP is confirmed.",
  request: { body: { content: { "application/json": { schema: PlatformAcceptInviteRequest } } } },
  responses: {
    200: {
      description: "Password set — TOTP enrollment is next",
      content: { "application/json": { schema: PlatformAcceptInviteResponse } },
    },
    400: problemResponse("Validation failed or PASSWORD_POLICY"),
    401: problemResponse("STAFF_INVITE_INVALID (unknown/revoked/already-accepted token)"),
    404: notAdminHost,
    410: problemResponse("STAFF_INVITE_EXPIRED"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/password-reset",
  operationId: "platformPasswordResetRequest",
  tags: ["platform"],
  summary: "Ask for a staff password-reset email",
  description:
    "Always 202 with no body, whatever the address: unknown, a stranger's, a disabled or a sandbox member where sandbox staff are not allowed, or throttled (3 an hour per address, 20 an hour per client address). A mail goes out only to an active member, or a totp_pending one whose second factor was never taken away (a single-use link valid 30 minutes), and a never-accepted bootstrap invite is re-sent instead. The link sets the password only; the TOTP or recovery-code step still stands at sign-in (spec 15 §3.1 step 7, D-587).",
  request: {
    body: { content: { "application/json": { schema: PlatformPasswordResetRequest } } },
  },
  responses: {
    202: { description: "Accepted (a mail is sent only when the member qualifies)" },
    400: problemResponse("Validation failed"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/password-reset/complete",
  operationId: "platformPasswordResetComplete",
  tags: ["platform"],
  summary: "Consume a staff reset link and set a new password",
  description:
    "Staff password policy (min 12 characters, zxcvbn score 3 or more). Success revokes every session of the member, voids their unspent login tickets, clears the lockout and supersedes every other open link. No session is minted: the member signs in, with TOTP (spec 15 §3.1 step 7, D-587).",
  request: {
    body: {
      content: { "application/json": { schema: PlatformPasswordResetCompleteRequest } },
    },
  },
  responses: {
    200: {
      description: "Password set — sign in next",
      content: { "application/json": { schema: PlatformPasswordResetCompleteResponse } },
    },
    400: problemResponse(
      "STAFF_RESET_INVALID (one code for unknown, used, superseded, expired or not usable now) or PASSWORD_POLICY",
    ),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/break-glass",
  operationId: "platformBreakGlass",
  tags: ["platform"],
  summary: "Lone-admin break-glass: reset the only platform_admin's second factor",
  description:
    "For the operator command `pnpm ops:break-glass` alone. The token is checked against a grant an AWS account administrator wrote into this deployment's SSM SecureString parameter in the last 10 minutes; SSM's own record of who wrote it and when is what the route trusts. A proven grant is consumed once, applied or refused, and the parameter is deleted. Applied: the target fleet platform_admin drops to totp_pending, their recovery codes, sessions and login tickets die, and a password link is mailed to them. Refused while another active fleet platform_admin exists (spec 15 §3.1 step 5 is the path then). Audited staff.break_glass_totp_reset or staff.break_glass_refused; every consumed grant pages (spec 15 §3.1 step 8, D-587).",
  request: { body: { content: { "application/json": { schema: PlatformBreakGlassRequest } } } },
  responses: {
    200: {
      description: "Second factor reset — the admin enrols again at next sign-in",
      content: { "application/json": { schema: PlatformBreakGlassResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse(
      "BREAK_GLASS_GRANT_INVALID (no grant, too old, malformed, wrong token or already consumed)",
    ),
    404: notAdminHost,
    409: problemResponse(
      "BREAK_GLASS_REFUSED (reason: target_unknown, target_not_fleet_admin, target_not_enrolled or another_admin)",
    ),
    429: problemResponse("TOO_MANY_ATTEMPTS"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/totp/enroll",
  operationId: "platformTotpEnroll",
  tags: ["platform"],
  summary: "Begin mandatory TOTP enrollment (staff in totp_pending)",
  security: staffSecurity,
  responses: {
    200: {
      description: "Secret generated (RFC 6238, SHA-1, 6 digits, 30 s)",
      content: { "application/json": { schema: PlatformTotpEnrollResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    404: notAdminHost,
    409: problemResponse("STAFF_NOT_TOTP_PENDING"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/totp/confirm",
  operationId: "platformTotpConfirm",
  tags: ["platform"],
  summary: "Confirm enrollment; returns the 10 recovery codes exactly once",
  security: staffSecurity,
  request: { body: { content: { "application/json": { schema: PlatformTotpConfirmRequest } } } },
  responses: {
    200: {
      description: "TOTP active — staff status becomes 'active'",
      content: { "application/json": { schema: PlatformRecoveryCodesResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("INVALID_TOTP_CODE"),
    404: notAdminHost,
    409: problemResponse("STAFF_NOT_TOTP_PENDING"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/auth/recovery-codes/regenerate",
  operationId: "platformRegenerateRecoveryCodes",
  tags: ["platform"],
  summary: "Reissue recovery codes (voids all unused; fresh TOTP required)",
  security: staffSecurity,
  request: {
    body: { content: { "application/json": { schema: PlatformRecoveryRegenerateRequest } } },
  },
  responses: {
    200: {
      description: "New batch of 10 (audited staff.recovery_codes_regenerated)",
      content: { "application/json": { schema: PlatformRecoveryCodesResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("INVALID_TOTP_CODE"),
    403: problemResponse("TOTP_ENROLLMENT_REQUIRED while the caller's TOTP is unconfirmed"),
    404: notAdminHost,
  },
});

// --- staff management (v0: list + invite) ---

registry.registerPath({
  method: "get",
  path: "/v1/platform/staff",
  operationId: "platformListStaff",
  tags: ["platform"],
  summary: "List platform staff",
  security: staffSecurity,
  responses: {
    200: {
      description: "Staff members",
      content: { "application/json": { schema: PlatformStaffListResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/staff",
  operationId: "platformInviteStaff",
  tags: ["platform"],
  summary: "Invite a staff member (platform_admin)",
  description:
    "Creates the row status='invited' and mails the one-time acceptance link (7-day expiry) — spec 15 §3.1.1.",
  security: staffSecurity,
  request: { body: { content: { "application/json": { schema: PlatformStaffInviteRequest } } } },
  responses: {
    201: {
      description: "Invited",
      content: { "application/json": { schema: PlatformStaffView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox inviter asked for fleet staff or a platform_admin (D-477)",
    ),
    404: notAdminHost,
    409: problemResponse("STAFF_EXISTS"),
    422: problemResponse(
      "SANDBOX_STAFF_NOT_ALLOWED: this deployment does not allow sandbox staff (D-477); or VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

// --- tenants ---

registry.registerPath({
  method: "get",
  path: "/v1/platform/tenants",
  operationId: "platformListTenants",
  tags: ["platform"],
  summary: "List tenants (all staff roles)",
  security: staffSecurity,
  request: {
    query: z.object({
      status: z.string().optional(),
      q: z.string().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(100).optional(),
    }),
  },
  responses: {
    200: {
      description: "Tenant summaries, newest first (cursor pagination)",
      content: { "application/json": { schema: PlatformTenantListResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/subdomains/{subdomain}/availability",
  operationId: "platformCheckSubdomain",
  tags: ["platform"],
  summary: "Is this subdomain free for a new tenant? (platform_admin)",
  description:
    "The create wizard's live check (spec 15 §4 S11): the subdomain pattern, then the reserved list, then tenants and aliases, read without a lock. It is advice for the form. The create decides under its own locks and still answers 409 SUBDOMAIN_TAKEN for a name claimed since.",
  security: staffSecurity,
  request: { params: z.object({ subdomain: z.string().min(1).max(63) }) },
  responses: {
    200: {
      description: "The verdict, with the reason when the name is not free",
      content: { "application/json": { schema: PlatformSubdomainCheckResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/vertical-packs",
  operationId: "platformListVerticalPacks",
  tags: ["platform"],
  summary: "Vertical packs a new tenant can start from (all staff roles)",
  description:
    "Served read-only from the deployed packages/verticals bundle, limited to the keys tenant create accepts (spec 15 §4 S11 pack cards).",
  security: staffSecurity,
  responses: {
    200: {
      description: "The packs, with names and descriptions in every locale the pack carries",
      content: { "application/json": { schema: PlatformVerticalPackListResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants",
  operationId: "platformCreateTenant",
  tags: ["platform"],
  summary: "Create a tenant (platform_admin; via TenancyService)",
  description:
    "Inserts the module-16 tenants row as `provisioning` + the tenant_meta row + the platform.tenant_created outbox event in one transaction; the provisioning worker then seeds, verifies DNS, mints the Group Admin invite, and flips the tenant active (spec 15 §3.2). Idempotency-Key honored against the staff-keyed store.",
  security: staffSecurity,
  request: { body: { content: { "application/json": { schema: PlatformCreateTenantRequest } } } },
  responses: {
    201: {
      description: "Tenant created in status 'provisioning'",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed or SUBDOMAIN_INVALID"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted"),
    404: notAdminHost,
    409: problemResponse("SUBDOMAIN_TAKEN or SUBDOMAIN_RESERVED"),
    422: problemResponse("PACK_UNKNOWN — not a packages/verticals registry key"),
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/tenants/{id}",
  operationId: "platformGetTenant",
  tags: ["platform"],
  summary: "Tenant detail (v0: tenant + meta + provisioning state)",
  security: staffSecurity,
  request: { params: z.object({ id: z.guid() }) },
  responses: {
    200: {
      description: "Tenant detail",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse("Unknown tenant"),
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/tenants/{id}/lifecycle-events",
  operationId: "platformTenantLifecycleEvents",
  tags: ["platform"],
  summary: "Tenant lifecycle timeline (spec 15 §2.5)",
  security: staffSecurity,
  request: {
    params: z.object({ id: z.guid() }),
    query: z.object({
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(100).optional(),
    }),
  },
  responses: {
    200: {
      description: "Newest-first lifecycle events",
      content: { "application/json": { schema: LifecycleEventsResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse("Unknown tenant"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants/{id}/suspend",
  operationId: "platformSuspendTenant",
  tags: ["platform"],
  summary: "Suspend a tenant (grace or immediate)",
  description:
    "platform_admin: any reason/mode. platform_billing: reason=billing_overdue, mode=grace only (spec 15 §7). Grace = 7-day read-only window; immediate additionally revokes the tenant's live sessions at once (spec 15 §3.2).",
  security: staffSecurity,
  request: {
    params: z.object({ id: z.guid() }),
    body: { content: { "application/json": { schema: PlatformSuspendRequest } } },
  },
  responses: {
    200: {
      description: "Suspended",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted (role or reason/mode outside the matrix), or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant"),
    409: problemResponse("INVALID_TENANT_STATUS"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants/{id}/restore",
  operationId: "platformRestoreTenant",
  tags: ["platform"],
  summary: "Restore a suspended tenant",
  description:
    "platform_billing may restore billing_overdue suspensions only; the §5 outstanding-balance guard (409 OUTSTANDING_BALANCE) arrives with the billing snapshot (deferred — ledger).",
  security: staffSecurity,
  request: {
    params: z.object({ id: z.guid() }),
    body: { content: { "application/json": { schema: PlatformRestoreRequest } } },
  },
  responses: {
    200: {
      description: "Restored",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant"),
    409: problemResponse("INVALID_TENANT_STATUS"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants/{id}/retry-provisioning",
  operationId: "platformRetryProvisioning",
  tags: ["platform"],
  summary: "Re-run failed provisioning from the last incomplete step",
  security: staffSecurity,
  request: { params: z.object({ id: z.guid() }) },
  responses: {
    200: {
      description: "Back in 'provisioning'; the worker resumes",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant"),
    409: problemResponse("PROVISIONING_NOT_FAILED"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "put",
  path: "/v1/platform/tenants/{id}/sandbox",
  operationId: "platformSetTenantSandbox",
  tags: ["platform"],
  summary: "Mark a tenant sandbox, or clear it (fleet platform_admin)",
  description:
    "A sandbox tenant is one sandbox staff may act on (D-477 ruling 2). Fleet platform_admin only; audited tenant.sandbox_set with before and after. Clearing the flag on a tenant that is on a sandbox plan is refused (409 PLAN_SANDBOX_ONLY): move it to a fleet plan first (D-615 ruling 3).",
  security: staffSecurity,
  request: {
    params: z.object({ id: z.guid() }),
    body: { content: { "application/json": { schema: PlatformSetSandboxRequest } } },
  },
  responses: {
    200: {
      description: "The tenant with its sandbox flag as set",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member (D-477)"),
    404: problemResponse("Unknown tenant"),
    409: problemResponse(
      "PLAN_SANDBOX_ONLY (clearing the flag of a tenant on a sandbox plan), IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants/{id}/resend-admin-invite",
  operationId: "platformResendAdminInvite",
  tags: ["platform"],
  summary: "Re-issue the Group Admin invite (revokes the prior live one)",
  security: staffSecurity,
  request: {
    params: z.object({ id: z.guid() }),
    body: { content: { "application/json": { schema: PlatformResendAdminInviteRequest } } },
  },
  responses: {
    200: {
      description: "Invite re-issued (a module-16 invite; 7-day expiry)",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant"),
    409: problemResponse("IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

// --- impersonation (spec 15 §3.7 steps 1–3, 5) ---

registry.registerPath({
  method: "post",
  path: "/v1/platform/impersonations",
  operationId: "platformCreateImpersonation",
  tags: ["platform"],
  summary: "Request an impersonation grant",
  description:
    "read_only (admin or support) activates immediately. act_as by platform_admin activates immediately; act_as by platform_support returns 202 approval_required and stays unusable until a platform_admin approves (spec 15 §3.7.2).",
  security: staffSecurity,
  request: { body: { content: { "application/json": { schema: ImpersonationCreateRequest } } } },
  responses: {
    201: {
      description: "Grant active",
      content: { "application/json": { schema: ImpersonationCreateResponse } },
    },
    202: {
      description: "Grant pending platform_admin approval",
      content: { "application/json": { schema: ImpersonationCreateResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant or target user"),
    409: problemResponse("A live grant already exists for this staff+tenant"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/impersonations/{id}/approve",
  operationId: "platformApproveImpersonation",
  tags: ["platform"],
  summary: "Approve a pending act_as grant (platform_admin)",
  security: staffSecurity,
  request: { params: z.object({ id: z.guid() }) },
  responses: {
    200: {
      description: "Approved — grant is active",
      content: { "application/json": { schema: ImpersonationGrantView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown grant"),
    409: problemResponse("GRANT_NOT_ACTIVE — not awaiting approval"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/impersonations/{id}/token",
  operationId: "platformMintImpersonationToken",
  tags: ["platform"],
  summary: "Mint the one-time redemption code (grant owner, active grant)",
  description:
    "Returns a redirect URL to the tenant subdomain carrying a single-use, 2-minute code; redeeming it there mints the tenant DB session with impersonation metadata (spec 15 §3.7.3 / 16 §3.1.4a). Audited impersonation.start.",
  security: staffSecurity,
  request: { params: z.object({ id: z.guid() }) },
  responses: {
    200: {
      description: "One-time redemption code minted",
      content: { "application/json": { schema: ImpersonationTokenResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not the grant owner, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown grant"),
    409: problemResponse("GRANT_NOT_APPROVED or GRANT_NOT_ACTIVE"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/impersonations/{id}/end",
  operationId: "platformEndImpersonation",
  tags: ["platform"],
  summary: "End/revoke a grant (owner or platform_admin) — kills the session",
  security: staffSecurity,
  request: { params: z.object({ id: z.guid() }) },
  responses: {
    200: {
      description: "Ended (owner) / revoked (admin); tenant session dead at its next request",
      content: { "application/json": { schema: ImpersonationGrantView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown grant"),
    409: problemResponse("GRANT_NOT_ACTIVE"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/impersonations",
  operationId: "platformListImpersonations",
  tags: ["platform"],
  summary: "List grants (all staff)",
  security: staffSecurity,
  request: {
    query: z.object({
      tenant_id: z.guid().optional(),
      staff_id: z.guid().optional(),
      status: z.string().optional(),
      /** The previous page's next_cursor (S10's history, M15-8). */
      cursor: z.string().min(1).max(512).optional(),
      limit: z.coerce.number().int().min(1).max(100).optional(),
    }),
  },
  responses: {
    200: {
      description:
        "Grants, newest first, a page at a time, each naming who opened it and whom it targets",
      content: { "application/json": { schema: ImpersonationListResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: notAdminHost,
  },
});

// --- support actions (M15-8, D-620; spec 15 §3.8, §4 S10) ---

const tenantPath = z.object({ id: z.guid() });
const tenantUserPath = z.object({ id: z.guid(), userId: z.guid() });

registry.registerPath({
  method: "get",
  path: "/v1/platform/tenants/{id}/users",
  operationId: "platformListTenantUsers",
  tags: ["platform"],
  summary: "A tenant's people, for support (platform_admin, platform_support)",
  description:
    "Through module 16's service (D-496 ruling 5): id, name, email, roles, last sign-in and whether the account is locked; nothing else. Agent principals are never listed. `q` matches name or address.",
  security: staffSecurity,
  request: {
    params: tenantPath,
    query: z.object({
      q: z.string().max(200).optional(),
      limit: z.coerce.number().int().min(1).max(100).optional(),
    }),
  },
  responses: {
    200: {
      description: "The matching people, by name",
      content: { "application/json": { schema: PlatformTenantUserListResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse("Unknown tenant"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants/{id}/users/{userId}/password-reset",
  operationId: "platformSendUserPasswordReset",
  tags: ["platform"],
  summary: "Mail a tenant user their own reset link (platform_admin, platform_support)",
  description:
    "Module 16 mails the reset link to the user's own address; the staff member never sees or sets a password (spec 15 §3.8). Audited support.password_reset_sent on the platform chain and password_reset_requested on the tenant's.",
  security: staffSecurity,
  request: { params: tenantUserPath },
  responses: {
    202: {
      description: "The link is on its way",
      content: { "application/json": { schema: PlatformSupportResetResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant, or no active member of it with that id"),
    409: problemResponse(
      "SUPPORT_RESET_NOT_ELIGIBLE with `reason` (not_a_person, user_not_active, email_unverified), or IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants/{id}/users/{userId}/revoke-sessions",
  operationId: "platformRevokeUserSessions",
  tags: ["platform"],
  summary: "End a tenant user's sessions in this tenant (platform_admin, platform_support)",
  description:
    "Ends the user's sessions pinned to this tenant or to none yet; a session the same person holds in another tenant is left alone (D-620). Audited support.sessions_revoked, scope user.",
  security: staffSecurity,
  request: { params: tenantUserPath },
  responses: {
    200: {
      description: "Sessions ended",
      content: { "application/json": { schema: PlatformRevokeSessionsResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant, or no active member of it with that id"),
    409: problemResponse("IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants/{id}/revoke-all-sessions",
  operationId: "platformRevokeTenantSessions",
  tags: ["platform"],
  summary: "End every session of a tenant (platform_admin)",
  description:
    "For security incidents (spec 15 §3.8). The typed confirmation must be the tenant's subdomain. Every live impersonation grant on the tenant is revoked with its sessions too. Audited support.sessions_revoked, scope tenant.",
  security: staffSecurity,
  request: {
    params: tenantPath,
    body: { content: { "application/json": { schema: PlatformRevokeAllSessionsRequest } } },
  },
  responses: {
    200: {
      description: "Sessions and grants ended",
      content: { "application/json": { schema: PlatformRevokeAllSessionsResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant"),
    409: problemResponse("IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS"),
    422: problemResponse(
      "CONFIRMATION_MISMATCH, or VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

// --- staff management (M15-9, D-621; spec 15 §3.1 step 5, §4 S17) ---

const staffPath = z.object({ id: z.guid() });
const staffForbiddenScope = problemResponse(
  "Not permitted, or STAFF_SCOPE_DENIED: a sandbox admin acting on fleet staff or making a platform_admin (D-477 ruling 5)",
);

registry.registerPath({
  method: "patch",
  path: "/v1/platform/staff/{id}",
  operationId: "platformUpdateStaff",
  tags: ["platform"],
  summary: "Change a staff member's role, disable or re-enable them (platform_admin)",
  description:
    "Disabling ends the member's sessions, login tickets, reset links and impersonation grants. Re-enabling forces a new second factor (spec 15 §3.1). Nobody changes themselves (STAFF_SELF_CHANGE), and the last active fleet platform_admin is never demoted or disabled (LAST_ADMIN).",
  security: staffSecurity,
  request: {
    params: staffPath,
    body: { content: { "application/json": { schema: PlatformStaffPatchRequest } } },
  },
  responses: {
    200: {
      description: "The member as changed",
      content: { "application/json": { schema: PlatformStaffView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbiddenScope,
    404: problemResponse("Unknown staff member"),
    409: problemResponse(
      "STAFF_SELF_CHANGE, LAST_ADMIN, STAFF_STATE_CONFLICT, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: problemResponse(
      "STAFF_PATCH_EMPTY, or VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/staff/{id}/resend-invite",
  operationId: "platformResendStaffInvite",
  tags: ["platform"],
  summary: "Re-issue a staff invite (platform_admin)",
  description: "A new token on the same row and seven fresh days; the prior token dies.",
  security: staffSecurity,
  request: { params: staffPath },
  responses: {
    200: {
      description: "Invite re-sent",
      content: { "application/json": { schema: PlatformStaffView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbiddenScope,
    404: problemResponse("Unknown staff member"),
    409: problemResponse(
      "INVITE_ALREADY_ACCEPTED, STAFF_SELF_CHANGE, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/staff/{id}/totp-reset",
  operationId: "platformResetStaffTotp",
  tags: ["platform"],
  summary: "Reset another staff member's second factor (platform_admin)",
  description:
    "The member drops to totp_pending and every session, login ticket, reset link and impersonation grant they hold ends (spec 15 §3.1 step 5). Audited staff.totp_reset with the reason.",
  security: staffSecurity,
  request: {
    params: staffPath,
    body: { content: { "application/json": { schema: PlatformStaffTotpResetRequest } } },
  },
  responses: {
    200: {
      description: "Second factor reset",
      content: { "application/json": { schema: PlatformStaffView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbiddenScope,
    404: problemResponse("Unknown staff member"),
    409: problemResponse(
      "STAFF_SELF_CHANGE, STAFF_STATE_CONFLICT (only an active member's second factor can be reset), IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

// --- the platform audit log (M15-9, D-622, D-623; spec 15 §4 S18) ---

registry.registerPath({
  method: "get",
  path: "/v1/platform/audit-log",
  operationId: "platformListAuditLog",
  tags: ["platform"],
  summary: "The platform audit chain, newest first (platform_admin, platform_readonly)",
  description:
    'Filter by staff member or actor, action prefix, tenant and time; a page at a time. With `Accept: application/x-ndjson` the whole filtered stream downloads as NDJSON (one row a line, up to 50,000, then a trailer line `{"export": {"rows", "truncated"}}`), and cursor and limit are refused.',
  security: staffSecurity,
  request: {
    query: z.object({
      staff_id: z.guid().optional(),
      actor: z.string().min(1).max(200).optional(),
      action_prefix: z.string().min(1).max(100).optional(),
      tenant_id: z.guid().optional(),
      from: isoDateTime().optional(),
      to: isoDateTime().optional(),
      cursor: z.string().min(1).max(64).optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: {
    200: {
      description: "A page of the chain, or the NDJSON export",
      content: {
        "application/json": { schema: PlatformAuditLogResponse },
        "application/x-ndjson": { schema: z.string() },
      },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: notAdminHost,
    422: problemResponse("INVALID_RANGE or EXPORT_TAKES_NO_PAGE"),
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/audit-log/verification",
  operationId: "platformAuditChainVerification",
  tags: ["platform"],
  summary: "The platform chain's newest verification (platform_admin, platform_readonly)",
  security: staffSecurity,
  responses: {
    200: {
      description: "Intact through which id, when, and whether the head is anchored",
      content: { "application/json": { schema: PlatformChainVerification } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/audit-log/verification",
  operationId: "platformVerifyAuditChain",
  tags: ["platform"],
  summary: "Walk and anchor the platform chain now (platform_admin)",
  description:
    "Verifies the chain from the last head proved intact and anchors the new head to the Object Lock bucket when the deployment has one (spec 15 §2.15a, D-623). Audited audit_chain.verified.",
  security: staffSecurity,
  responses: {
    200: {
      description: "The verification just recorded",
      content: { "application/json": { schema: PlatformChainVerification } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted (platform_admin only)"),
    404: notAdminHost,
    409: problemResponse("IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS"),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

// --- tenant-side redemption (spec 16 §3.1.4a; answers on tenant hosts) ---

registry.registerPath({
  method: "post",
  path: "/v1/auth/impersonation/redeem",
  operationId: "redeemImpersonationCode",
  tags: ["identity"],
  summary: "Redeem a one-time impersonation code on the tenant subdomain",
  description:
    "Mints an ordinary DB session row carrying impersonation_grant_id/mode (no JWTs — revocation semantics are the session row's, spec 16 §3.1.4a). Single use; the code dies after 2 minutes. Generic 401 IMPERSONATION_CODE_INVALID on any failure. Part of the /v1/auth/* Idempotency-Key exemption group.",
  request: { body: { content: { "application/json": { schema: ImpersonationRedeemRequest } } } },
  responses: {
    200: {
      description: "Impersonation session established (Set-Cookie, tenant space)",
      content: { "application/json": { schema: ImpersonationRedeemResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("IMPERSONATION_CODE_INVALID"),
  },
});

// --- tenant health and the console's logs (spec 15 §2.21, §2.22, §3.10, §4
// S9 and S15; M15-4, D-611 to D-613) ---

registry.registerPath({
  method: "get",
  path: "/v1/tenant-probe",
  operationId: "tenantProbe",
  tags: ["platform"],
  summary: "The health collector's probe of one tenant (tenant hosts only)",
  description:
    "Answered on a tenant subdomain, and only to a request carrying X-Assessly-Probe: `<minute>.<hex HMAC-SHA256 over host and minute>` under the deployment's probe key (D-481 ruling 5). Without a valid signature it answers exactly as an unknown path does. With one, it reads the tenant through a tenant transaction and answers 200, or 503 HEALTH_DRILL while a health drill is on (D-612). Excluded from the request meter.",
  responses: {
    200: {
      description: "The tenant answered",
      content: { "application/json": { schema: TenantProbeResponse } },
    },
    400: undeclaredQueryRefused,
    404: problemResponse("No valid probe signature, or not a tenant host: as an unknown path"),
    503: problemResponse(
      "HEALTH_DRILL, or DEPENDENCY_UNAVAILABLE when the database did not answer",
    ),
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/health",
  operationId: "platformHealthBoard",
  tags: ["platform"],
  summary: "The fleet health board, worst first (all staff roles)",
  description:
    "Every active tenant with the latest collected window, its dot and reasons, 24-hour uptime and open alerts, sorted red, amber, grey, green and within each by downtime, lag and error rate (spec 15 §4 S15, D-612). The window is stale when none was collected in 15 minutes; a stale window reads grey for every tenant.",
  security: staffSecurity,
  responses: {
    200: {
      description: "The board",
      content: { "application/json": { schema: PlatformHealthBoardResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/tenants/{id}/health",
  operationId: "platformTenantHealth",
  tags: ["platform"],
  summary: "One tenant's health series (all staff roles)",
  description:
    "24h returns the last day's snapshots in `windows`; 7d and 30d return the daily rollups in `days` (spec 15 §2.21, §2.22, §4 S9). The current reading, the open and recent alerts, and the fleet backup reading ride along.",
  security: staffSecurity,
  request: {
    params: z.object({ id: z.guid() }),
    query: z.object({ window: PlatformHealthSeriesWindow.optional() }),
  },
  responses: {
    200: {
      description: "The tenant's health",
      content: { "application/json": { schema: PlatformTenantHealthResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse("Unknown tenant"),
  },
});

registry.registerPath({
  method: "put",
  path: "/v1/platform/tenants/{id}/health-drill",
  operationId: "platformSetTenantHealthDrill",
  tags: ["platform"],
  summary: "Start or stop a health drill on a sandbox tenant (platform_admin)",
  description:
    "While a drill is on, the tenant's probe answers 503 HEALTH_DRILL, so the collector records it down and the board, the dot and the alert rows show a real failure; its alerts never page (D-612). A drill needs a sandbox tenant (409 HEALTH_DRILL_NOT_SANDBOX). platform_admin only, fenced like every tenant mutation (D-477); audited tenant.health_drill_set with before and after.",
  security: staffSecurity,
  request: {
    params: z.object({ id: z.guid() }),
    body: { content: { "application/json": { schema: PlatformSetHealthDrillRequest } } },
  },
  responses: {
    200: {
      description: "The tenant with its drill as set",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant"),
    409: problemResponse(
      "HEALTH_DRILL_NOT_SANDBOX, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: problemResponse(
      "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/logs/errors",
  operationId: "platformLogErrors",
  tags: ["platform"],
  summary: "Server errors the request meter saw, newest first (all staff roles)",
  description:
    "The console's Logs view (D-613): the 5xx responses of the last hour, day or week, fleet-wide or for one tenant, kept 14 days. A fixed query: no free-form search. The request id finds the same request in the API's logs.",
  security: staffSecurity,
  request: {
    query: z.object({
      tenant_id: z.guid().optional(),
      since: PlatformLogSince.optional(),
      cursor: z.guid().optional(),
      limit: z.coerce.number().int().min(1).max(100).optional(),
    }),
  },
  responses: {
    200: {
      description: "A page of server errors",
      content: { "application/json": { schema: PlatformRequestErrorListResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse(
      "A tenant_id that names no tenant, or requested on a host other than the admin host",
    ),
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/logs/alerts",
  operationId: "platformLogAlerts",
  tags: ["platform"],
  summary: "Health alerts, newest first (all staff roles)",
  description:
    "The console's Logs view (D-613): every health alert the collector opened, open or resolved, fleet-wide or for one tenant (spec 15 §3.10, D-611).",
  security: staffSecurity,
  request: {
    query: z.object({
      tenant_id: z.guid().optional(),
      state: z.enum(["open", "all"]).optional(),
      cursor: z.guid().optional(),
      limit: z.coerce.number().int().min(1).max(100).optional(),
    }),
  },
  responses: {
    200: {
      description: "A page of alerts",
      content: { "application/json": { schema: PlatformHealthAlertListResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse(
      "A tenant_id that names no tenant, or requested on a host other than the admin host",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/impersonation/end",
  operationId: "endImpersonationSession",
  tags: ["identity"],
  summary: "End the caller's own impersonation session (the S19 banner's End)",
  description:
    "For an impersonation session only: ends the grant it was minted under as its owner would (audited impersonation.end on the platform chain), ends every session minted under that grant and clears the cookie. Allowed under a read-only grant. Part of the /v1/auth/* Idempotency-Key exemption group.",
  responses: {
    200: {
      description: "Ended; the cookie is cleared",
      content: { "application/json": { schema: ImpersonationEndResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    404: problemResponse("This session is not an impersonation session"),
  },
});
